import { useEffect, useState } from 'react';

/**
 * Whether the user has asked for reduced motion.
 *
 * The CSS side of this is already handled globally — the
 * `@media (prefers-reduced-motion: reduce)` block in `src/styles/tokens.css`
 * clamps every animation and transition duration with `!important`, so no CSS
 * animation needs to opt in individually.
 *
 * This hook exists for the motion that block cannot reach: JavaScript-driven
 * animation. `useCountUp` tweens a number in a `requestAnimationFrame` loop, and
 * no stylesheet can switch that off.
 *
 * The `matchMedia` guard mirrors `useTheme` — it is missing in jsdom and in some
 * older webviews, and a hard reference would take the whole render down.
 */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => query()?.matches ?? false);

  useEffect(() => {
    const media = query();
    if (!media) return;
    const onChange = () => setReduced(media.matches);
    // Read once on mount too: the preference can change between the initial
    // state and this effect, and the OS toggle fires no event for that gap.
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

function query(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia('(prefers-reduced-motion: reduce)');
}
